import React, { useEffect } from 'react'
import { motion } from 'framer-motion'
import { WheelOption } from '../types'
// import spinSoundUrl from '../assets/spin.mp3'
const spinSoundUrl = '/audio/spin.wav'

interface WheelProps {
  options: WheelOption[]
  isSpinning: boolean
  result: WheelOption | null
  onSpin: () => void
  onSpinEnd?: () => void
  mode: 'normal' | 'elimination' | 'accumulation'
  pendingElimination?: WheelOption | null
  soundEnabled: boolean
}

const colors = [
  '#3B82F6', '#8B5CF6', '#EF4444', '#10B981', 
  '#F59E0B', '#EC4899', '#06B6D4', '#84CC16',
  '#F97316', '#A855F7', '#14B8A6', '#F43F5E',
  '#6366F1', '#8B5CF6', '#06B6D4', '#10B981'
]

export const Wheel: React.FC<WheelProps> = ({ options, isSpinning, result, onSpin, onSpinEnd, mode, pendingElimination, soundEnabled }) => {
  // 已无未用useState声明

  useEffect(() => {
    if (mode === 'elimination' && !isSpinning && pendingElimination) {
      const timer = setTimeout(() => {
        if (onSpinEnd) onSpinEnd()
      }, 1000)
      return () => clearTimeout(timer)
    }
  }, [mode, isSpinning, pendingElimination, onSpinEnd])
  
  useEffect(() => {
    if (isSpinning && soundEnabled) {
      const audio = new Audio(spinSoundUrl)
      audio.play()
    }
  }, [isSpinning, soundEnabled])
  
  // Calculate current pointed option (top position)
  const getCurrentPointedOption = () => {
    if (isSpinning || !result) return null
    return result
  }
  
  const currentPointed = getCurrentPointedOption()
  

  
  // Calculate wheel final rotation angle
  const getFinalRotation = () => {
    if (!result) return 0
    
    // Find result option position in wheel
    const resultIndex = options.findIndex(option => option.id === result.id)
    if (resultIndex === -1) return 0
    
    // Calculate option center angle (from top, clockwise)
    const anglePerOption = 360 / options.length
    const optionCenterAngle = resultIndex * anglePerOption + anglePerOption / 2
    
    // Since pointer is at top (12 o'clock position), need to align option center to top
    // Wheel needs counterclockwise rotation, so negative angle
    // Since options start from top, need to align selected option to top pointer
    const finalRotation = -optionCenterAngle
    

    
    return finalRotation
  }
  
  // Calculate spin animation final angle
  const getSpinFinalAngle = () => {
    // Wheel needs to rotate multiple times then stop at specified position
    const baseRotation = 360 * 8 // 8 clockwise rotations
    
    if (!result) {
      // If no result, stop at random position
      const randomRotation = Math.random() * 360
      return baseRotation + randomRotation
    }
    
    // Find result option position in wheel
    const resultIndex = options.findIndex(option => option.id === result.id)
    if (resultIndex === -1) {
      // If result not found, stop at random position
      const randomRotation = Math.random() * 360
      return baseRotation + randomRotation
    }
    
    // Calculate option center angle (from top, clockwise)
    const anglePerOption = 360 / options.length
    const optionCenterAngle = resultIndex * anglePerOption + anglePerOption / 2
    
    // Final position: align option center to top pointer
    // Note: SVG coordinate system, 0 degrees at right, 90 degrees at bottom, so need adjustment
    // Since options start from top, need to align selected option to top pointer
    const finalPosition = -optionCenterAngle
    const finalAngle = baseRotation + finalPosition
    

    
    return finalAngle
  }
  if (options.length === 0) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="relative w-32 h-32 border-4 border-dashed border-gray-300 rounded-full flex items-center justify-center mb-4 bg-gradient-to-br from-gray-50 to-gray-100">
            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-primary-100 to-secondary-100 opacity-20"></div>
            <span className="text-gray-500 text-sm font-medium relative z-10">Add Options</span>
            {/* Decorative pointer */}
            <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1">
              <div className="w-0 h-0 border-l-6 border-r-6 border-b-10 border-l-transparent border-r-transparent border-b-red-400"></div>
            </div>
          </div>
          <p className="text-gray-600 mb-2">Please add some options first</p>
          <p className="text-gray-400 text-sm">Add your decision options in the left input field</p>
        </div>
      </div>
    )
  }

  const anglePerOption = 360 / options.length

  return (
    <div className="flex flex-col items-center space-y-8">
      <div className="relative">
        {/* Wheel outer ring indicator */}
        <div className="absolute inset-0 rounded-full border-2 border-red-500 border-opacity-20 pointer-events-none"></div>
        <motion.div
          className="relative w-80 h-80"
          initial={{ rotate: 0 }}
          animate={isSpinning ? {
            rotate: getSpinFinalAngle()
          } : {
            rotate: getFinalRotation()
          }}
          transition={{
            duration: isSpinning ? 6 : 0,
            ease: isSpinning ? "easeOut" : "easeInOut"
          }}
          onAnimationComplete={() => {
            if (isSpinning && onSpinEnd) {
              onSpinEnd()
            }
          }}
          style={{
            filter: isSpinning ? 'drop-shadow(0 0 20px rgba(59, 130, 246, 0.3))' : 'drop-shadow(0 0 10px rgba(0, 0, 0, 0.1))'
          }}
        >
          <svg
            className="w-full h-full"
            viewBox="0 0 100 100"
          >
            {options.map((option, index) => {
              // From top, clockwise arrangement
              const startAngle = index * anglePerOption
              const endAngle = (index + 1) * anglePerOption
              // Convert to SVG coordinate system (-90 degree offset, make top starting position)
              const startRad = (startAngle - 90) * Math.PI / 180
              const endRad = (endAngle - 90) * Math.PI / 180
              
              const x1 = 50 + 40 * Math.cos(startRad)
              const y1 = 50 + 40 * Math.sin(startRad)
              const x2 = 50 + 40 * Math.cos(endRad)
              const y2 = 50 + 40 * Math.sin(endRad)
              
              const largeArcFlag = anglePerOption > 180 ? 1 : 0
              
              return (
                <g key={option.id}>
                  <path
                    d={`M 50 50 L ${x1} ${y1} A 40 40 0 ${largeArcFlag} 1 ${x2} ${y2} Z`}
                    fill={colors[index % colors.length]}
                    stroke="#fff"
                    strokeWidth="1"
                    style={{
                      filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))'
                    }}
                  />
                  <text
                    x={50 + 25 * Math.cos((startAngle + anglePerOption / 2 - 90) * Math.PI / 180)}
                    y={50 + 25 * Math.sin((startAngle + anglePerOption / 2 - 90) * Math.PI / 180)}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fill="white"
                    fontSize="3"
                    fontWeight="bold"
                    className="pointer-events-none"
                    style={{
                      textShadow: '2px 2px 4px rgba(0, 0, 0, 0.7)',
                      filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.5))'
                    }}
                  >
                    {option.text}
                  </text>
                </g>
              )
            })}
          </svg>
        </motion.div>

        {/* Simple pointer */}
        <div className="absolute top-0 left-1/2 transform -translate-x-1/2 z-20">
          <div className="w-0 h-0 border-l-8 border-r-8 border-b-12 border-l-transparent border-r-transparent border-b-red-600 drop-shadow-lg"></div>
        </div>
        
        {/* Wheel center decoration */}
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-5">
          <div className="w-8 h-8 bg-white rounded-full border-4 border-gray-300 shadow-lg flex items-center justify-center">
            <div className="w-4 h-4 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-full"></div>
          </div>
        </div>
        
        {/* Top indicator line */}
        <div className="absolute top-0 left-1/2 transform -translate-x-1/2 z-15">
          <div className="w-1 h-6 bg-red-600 rounded-full"></div>
        </div>
        

      </div>

      <div className="flex flex-col items-center space-y-4">
        {/* Current pointed indicator */}
        {currentPointed && !isSpinning && (
          <div className="bg-red-100 border-2 border-red-300 rounded-lg px-4 py-2 text-center">
            <p className="text-red-800 font-medium">
              🎯 Currently pointing to: <span className="font-bold">{currentPointed.text}</span>
            </p>
          </div>
        )}
        
        <div className="flex space-x-4">
          <button
            onClick={onSpin}
            disabled={isSpinning || options.length === 0}
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed px-8 py-3 text-lg"
          >
            {isSpinning ? 'Spinning...' : 'Start Spin'}
          </button>
        </div>
      </div>

    </div>
  )
} 